import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import {
  GRUPOS_AROMA_LABELS,
  TAXONOMIA_AROMAS,
  type FamiliaAroma,
  type GrupoAromaKey,
  type SeleccionDescriptores,
} from '@escuela/ficha-domain'
import { ScoreSlider } from './score-slider'

/**
 * Slider "Condición de la fruta" anidado dentro del cuadro de la familia Frutal
 * (paridad con la app). Se muestra al pie del panel Frutal cuando hay algun
 * descriptor frutal seleccionado.
 */
export interface FruitCondition {
  value: number
  onChange: (value: number) => void
  labels?: { min: string; max: string }
  ariaLabel: string
  /** Path del registry, para el marcador `data-field-path` (tests de paridad). */
  fieldPath: string
}

interface DescriptorTreeProps {
  value: SeleccionDescriptores
  onChange: (value: SeleccionDescriptores) => void
  /** Color del vino (apariencia.colorPrincipal) para filtrar Evolución. */
  wineColor?: string
  /** Slider de condición de la fruta, anidado en el cuadro Frutal. */
  fruitCondition?: FruitCondition
}

const GRUPOS: readonly GrupoAromaKey[] = ['primarios', 'secundarios', 'terciarios']

function familiaDescriptores(fam: FamiliaAroma): readonly string[] {
  if (fam.descriptores) return fam.descriptores
  if (fam.subfamilias) return fam.subfamilias.flatMap((s) => s.descriptores)
  return []
}

/**
 * Para "evolucion", si ya se conoce el color (tinto/blanco) muestra solo su
 * subfamilia. Paridad con la app (`descriptor-selector.tsx`).
 */
function visibleSubfamilias(fam: FamiliaAroma, wineColor?: string) {
  if (!fam.subfamilias) return undefined
  if (fam.id === 'evolucion' && (wineColor === 'tinto' || wineColor === 'blanco')) {
    const wanted = wineColor === 'tinto' ? 'vinos-tintos' : 'vinos-blancos'
    return fam.subfamilias.filter((sf) => sf.id === wanted)
  }
  return fam.subfamilias
}

/**
 * Arbol/acordeon de descriptores (aromas/sabores). grupo -> familia ->
 * descriptores multiselect + "otros" libre. Escribe la misma estructura de
 * datos que el DescriptorSelector de la app (`SeleccionDescriptores`).
 */
export function DescriptorTree({
  value,
  onChange,
  wineColor,
  fruitCondition,
}: DescriptorTreeProps): ReactNode {
  const [open, setOpen] = useState<Record<string, boolean>>({})

  const toggleOpen = (key: string): void =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }))

  const familiaSel = (grupo: GrupoAromaKey, famId: string) =>
    value[grupo]?.[famId] ?? { seleccionados: [], otros: '' }

  const setFamilia = (
    grupo: GrupoAromaKey,
    famId: string,
    next: { seleccionados: string[]; otros: string },
  ): void => {
    const grupoSel = { ...(value[grupo] ?? {}) }
    if (next.seleccionados.length === 0 && next.otros.trim() === '') {
      delete grupoSel[famId]
    } else {
      grupoSel[famId] = next
    }
    onChange({ ...value, [grupo]: grupoSel })
  }

  const toggleDescriptor = (grupo: GrupoAromaKey, famId: string, d: string): void => {
    const sel = familiaSel(grupo, famId)
    const seleccionados = sel.seleccionados.includes(d)
      ? sel.seleccionados.filter((x) => x !== d)
      : [...sel.seleccionados, d]
    setFamilia(grupo, famId, { seleccionados, otros: sel.otros ?? '' })
  }

  return (
    <div className="flex flex-col gap-4">
      {GRUPOS.map((grupo) => (
        <div key={grupo} className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {GRUPOS_AROMA_LABELS[grupo]}
          </h4>
          <div className="flex flex-col gap-1.5">
            {TAXONOMIA_AROMAS[grupo].map((fam) => {
              const key = `${grupo}.${fam.id}`
              const sel = familiaSel(grupo, fam.id)
              const count = sel.seleccionados.length
              const hasSelection = count > 0 || !!sel.otros
              // Las familias con seleccion arrancan abiertas (muestran lo elegido
              // y, en Frutal, el slider de condicion de la fruta).
              const isOpen = open[key] ?? hasSelection
              return (
                <div key={key} className="rounded-md border bg-background">
                  <button
                    type="button"
                    onClick={() => toggleOpen(key)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium"
                    aria-expanded={isOpen}
                  >
                    <span className="flex items-center gap-2">
                      {fam.label}
                      {count > 0 ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                          {count}
                        </span>
                      ) : null}
                    </span>
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                    />
                  </button>
                  {isOpen ? (
                    <div className="flex flex-col gap-3 border-t p-3">
                      {visibleSubfamilias(fam, wineColor) ? (
                        visibleSubfamilias(fam, wineColor)!.map((sub) => (
                          <div key={sub.id} className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground">
                              {sub.label}
                            </span>
                            <DescriptorChips
                              descriptores={sub.descriptores}
                              selected={sel.seleccionados}
                              onToggle={(d) => toggleDescriptor(grupo, fam.id, d)}
                            />
                          </div>
                        ))
                      ) : (
                        <DescriptorChips
                          descriptores={familiaDescriptores(fam)}
                          selected={sel.seleccionados}
                          onToggle={(d) => toggleDescriptor(grupo, fam.id, d)}
                        />
                      )}
                      <Input
                        value={sel.otros ?? ''}
                        onChange={(e) =>
                          setFamilia(grupo, fam.id, {
                            seleccionados: sel.seleccionados,
                            otros: e.target.value,
                          })
                        }
                        placeholder="Otros (texto libre)"
                      />
                      {fam.id === 'frutal' && fruitCondition && hasSelection ? (
                        <div
                          data-field-path={fruitCondition.fieldPath}
                          className="flex flex-col gap-1.5 border-t pt-3"
                        >
                          <span className="text-xs font-medium text-muted-foreground">
                            {fruitCondition.ariaLabel}
                          </span>
                          <ScoreSlider
                            value={fruitCondition.value}
                            onChange={fruitCondition.onChange}
                            leftLabel={fruitCondition.labels?.min}
                            rightLabel={fruitCondition.labels?.max}
                            ariaLabel={fruitCondition.ariaLabel}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function DescriptorChips({
  descriptores,
  selected,
  onToggle,
}: {
  descriptores: readonly string[]
  selected: string[]
  onToggle: (d: string) => void
}): ReactNode {
  return (
    <div className="flex flex-wrap gap-1.5">
      {descriptores.map((d) => {
        const active = selected.includes(d)
        return (
          <button
            key={d}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(d)}
            className={cn(
              'rounded-full border px-2.5 py-1 text-xs transition-colors',
              active
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {d}
          </button>
        )
      })}
    </div>
  )
}
